import React, { useState, useEffect } from 'react';
import { baseURL, get, post, del } from './apiClient';
import {
  Container, Grid, Card, CardHeader, CardContent,
  TextField, Box, Alert, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, CircularProgress, IconButton, Button, Typography
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// Helper to format Firestore Timestamps and date strings
const formatDate = (timestamp) => {
  if (timestamp && typeof timestamp.seconds === 'number') {
    return new Date(timestamp.seconds * 1000).toLocaleString();
  }
  if (typeof timestamp === 'string') {
    const date = new Date(timestamp);
    if (!isNaN(date)) {
      return date.toLocaleString();
    }
  }
  return 'N/A';
};

export default function DashboardPage() {
  const [apiKeys, setApiKeys] = useState([]);
  const [newKey, setNewKey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentMessage, setPaymentMessage] = useState(null);
  const [newKeyName, setNewKeyName] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // Check for payment status query params on mount
  useEffect(() => {
    const query = new URLSearchParams(location.search);
    if (query.get("payment_success")) {
      setPaymentMessage({
        severity: "success",
        text: "Your payment method has been saved successfully!",
      });
    }
    if (query.get("payment_cancel")) {
      setPaymentMessage({
        severity: "warning",
        text: "The process was cancelled. You can add a payment method at any time.",
      });
    }
  }, [location.search]);

  useEffect(() => {
    const fetchKeys = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await get('/api-keys');
        setApiKeys(data.apiKeys || []);
      } catch (err) {
        setError('Failed to fetch API keys.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchKeys();
  }, []);

  const handleCreateKey = async (e) => {
    e.preventDefault();
    if (!newKeyName) return;

    try {
      setError(null);
      const data = await post('/api-keys', { key_name: newKeyName });
      setNewKey(data.new_key);
      setApiKeys(data.apiKeys || []);
      setNewKeyName(''); // Reset the input field
    } catch (err) {
      setError('Failed to create API key.');
      console.error(err);
    }
  };

  const handleDeleteKey = async (prefix) => {
    if (!window.confirm('Are you sure you want to delete this key?')) {
      return;
    }
    try {
      setError(null);
      await del(`/api-keys/${prefix}`);
      setApiKeys(apiKeys.filter(key => key.prefix !== prefix));
    } catch (err) {
      setError('Failed to delete API key.');
      console.error(err);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" onClick={() => navigate('/create-job')}>Create New Job</Button>
            <Button variant="outlined" onClick={() => navigate('/jobs')}>View Jobs</Button>
          </Box>
        </Grid>

        {paymentMessage && (
          <Grid item xs={12}>
            <Alert severity={paymentMessage.severity} onClose={() => setPaymentMessage(null)}>
              {paymentMessage.text}
            </Alert>
          </Grid>
        )}

        {error && <Grid item xs={12}><Alert severity="error">{error}</Alert></Grid>}

        {newKey && (
          <Grid item xs={12}>
            <Alert severity="success" onClose={() => setNewKey(null)}>
              <strong>New API Key Generated:</strong> Please copy this key and store it securely. You will not be able to see it again.
              <Box sx={{ fontFamily: 'monospace', my: 2, p: 1, bgcolor: 'grey.200', borderRadius: 1, display: 'flex', alignItems: 'center' }}>
                {newKey}
                <IconButton size="small" onClick={() => navigator.clipboard.writeText(newKey)}>
                  <ContentCopyIcon fontSize="inherit" />
                </IconButton>
              </Box>
              <Typography variant="h6" component="h5" sx={{ mt: 2 }}>Example: Create a Job</Typography>
              <Paper variant="outlined" sx={{ p: 2, mt: 1, backgroundColor: '#f5f5f5' }}>
                <Box component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', m: 0, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                  <code>
{`curl ${baseURL}/jobs \\
-H "Authorization: Bearer ${newKey}" \\
-H "Content-Type: application/json" \\
-d '{
  "url": "https://www.example.com",
  "product_category": "Your Product Category"
}'`}
                  </code>
                </Box>
              </Paper>
            </Alert>
          </Grid>
        )}

        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="Create New API Key" />
            <CardContent>
              <Box component="form" onSubmit={handleCreateKey} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Key Name"
                  variant="outlined"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  placeholder="e.g., My Test Key"
                  required
                />
                <Button type="submit" variant="contained">Create Key</Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader title="How to Use the API" />
            <CardContent>
              <Typography variant="body2" gutterBottom>
                To use the query generation API, you need to make authenticated requests using one of your generated API keys.
              </Typography>
              <Typography variant="subtitle1" component="h5" sx={{ mt: 2 }}>Authentication</Typography>
              <Paper variant="outlined" sx={{ p: 1, mt: 1, backgroundColor: '#f5f5f5' }}>
                <code style={{ fontFamily: 'monospace' }}>Authorization: Bearer sk_live_...</code>
              </Paper>
              <Typography variant="subtitle1" component="h5" sx={{ mt: 2 }}>1. Create a Job</Typography>
               <Paper variant="outlined" sx={{ p: 2, mt: 1, backgroundColor: '#f5f5f5' }}>
                <Box component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', m: 0, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                  <code>
{`curl ${baseURL}/jobs \\
-H "Authorization: Bearer YOUR_API_KEY" \\
-H "Content-Type: application/json" \\
-d '{
  "url": "https://www.example.com",
  "product_category": "Your Product Category"
}'`}
                  </code>
                </Box>
              </Paper>
               <Typography variant="subtitle1" component="h5" sx={{ mt: 2 }}>2. Check Job Status</Typography>
               <Paper variant="outlined" sx={{ p: 2, mt: 1, backgroundColor: '#f5f5f5' }}>
                <Box component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', m: 0, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                  <code>
{`curl ${baseURL}/jobs/YOUR_JOB_ID \\
-H "Authorization: Bearer YOUR_API_KEY"`}
                  </code>
                </Box>
              </Paper>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card>
            <CardHeader title="Your API Keys" />
            <CardContent>
              {loading ? (
                <CircularProgress />
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Prefix</TableCell>
                        <TableCell>Created At</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {apiKeys.length > 0 ? (
                        apiKeys.map(key => (
                          <TableRow key={key.prefix}>
                            <TableCell>{key.name}</TableCell>
                            <TableCell><code>{key.prefix}...</code></TableCell>
                            <TableCell>{formatDate(key.createdAt)}</TableCell>
                            <TableCell align="right">
                              <IconButton onClick={() => handleDeleteKey(key.prefix)} color="warning">
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} align="center">No API keys found.</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
